"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import gsap from "gsap";

interface BottleVisualProps {
  src: string;
  alt: string;
  name: string;
  glow: string;
  className?: string;
  sizes?: string;
  priority?: boolean;
  floating?: "float-slow" | "float-slower" | "";
  showNameplate?: boolean;
}

export default function BottleVisual({
  src,
  alt,
  name,
  glow,
  className = "",
  sizes,
  priority = false,
  floating = "float-slow",
  showNameplate = true,
}: BottleVisualProps) {
  const tiltRef = useRef<HTMLDivElement>(null);

  // Refined 2-3px float + whisper-subtle cursor tilt
  useEffect(() => {
    const el = tiltRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const rx = gsap.quickTo(el, "rotationX", { duration: 1.1, ease: "power3.out" });
    const ry = gsap.quickTo(el, "rotationY", { duration: 1.1, ease: "power3.out" });

    const handleMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width - 0.5;
      const py = (e.clientY - rect.top) / rect.height - 0.5;
      ry(px * 3.4);
      rx(-py * 3.4);
    };

    const handleLeave = () => {
      rx(0);
      ry(0);
    };

    el.addEventListener("mousemove", handleMove);
    el.addEventListener("mouseleave", handleLeave);
    return () => {
      el.removeEventListener("mousemove", handleMove);
      el.removeEventListener("mouseleave", handleLeave);
    };
  }, []);

  return (
    <div
      className={`group relative w-full ${className}`}
      style={{ perspective: "1000px" }}
      data-cursor="pointer"
    >
      <div
        ref={tiltRef}
        className={`relative aspect-[3/4] overflow-hidden rounded-sm ${floating}`}
        style={{
          transformStyle: "preserve-3d",
          background:
            "linear-gradient(180deg, #fdfbf7 0%, #f5f0e7 45%, #efe8dc 100%)",
          boxShadow: "0 26px 60px rgba(122, 100, 66, 0.18)",
          border: "1px solid rgba(255,255,255,0.7)",
        }}
      >
        {/* Studio gel aura — soft pink / elegant blue */}
        <div
          className="bottle-glow absolute inset-0 transition-opacity duration-700 opacity-50 group-hover:opacity-80"
          style={{
            background: `radial-gradient(ellipse 62% 55% at 50% 46%, ${glow}33 0%, transparent 68%)`,
          }}
          aria-hidden="true"
        />

        {/* Marble podium */}
        <div className="marble-pedestal" aria-hidden="true" />

        {/* Moving sunlight reflection across glass (prompt: light sweep) */}
        <div
          className="light-sweep light-sweep-animated absolute inset-y-0 left-0 w-1/3 opacity-40 group-hover:opacity-90 transition-opacity duration-700 pointer-events-none"
          style={{
            background:
              "linear-gradient(105deg, transparent, rgba(255,255,255,0.75), rgba(255,244,214,0.35))",
          }}
          aria-hidden="true"
        />

        {/* Static soft highlight on glass */}
        <div
          className="absolute left-[13%] top-[8%] bottom-[30%] w-[9%] bg-gradient-to-b from-white/80 via-white/40 to-transparent rounded-full"
          aria-hidden="true"
        />

        {/* Main bottle */}
        <div className="relative h-full w-full transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]">
          <Image
            src={src}
            alt={alt}
            fill
            priority={priority}
            className="object-contain object-center drop-shadow-[0_16px_30px_rgba(122,100,66,0.28)]"
            sizes={sizes}
          />
        </div>

        {/* Under-glare reflection */}
        <div
          className="bottle-under-glare"
          style={{ background: `radial-gradient(ellipse at center, ${glow}55 0%, transparent 70%)` }}
          aria-hidden="true"
        />
      </div>

      {/* Glass body label */}
      <div className="pointer-events-none absolute left-2 right-2 -bottom-5 md:-bottom-6 flex justify-center">
        {showNameplate && (
          <div className="glass-dark flex items-center gap-3 px-4 py-2 rounded-sm opacity-90 transition-opacity duration-500 group-hover:opacity-100">
            <span className="text-[8px] tracking-[0.3em] uppercase text-[#221d17]/75">
              {name}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}